/**
 * /mi-cuenta — Página de gestión de cuenta personal
 *
 * Secciones:
 *  1. Mi información (datos básicos del perfil)
 *  2. Seguridad y acceso (método auth + email personal de respaldo)
 *  3. Mis datos — exportar todo / eliminar cuenta
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, EmailAuthProvider } from "@/firebase";
import { reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { collection, getDocs, doc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { syncF1 } from "@/services/backendSync";
import {
  ArrowLeft, User, Mail, Shield, Download, Trash2,
  AlertTriangle, CheckCircle, Loader2, Clock,
  Building2, Lock, Pencil, ChevronDown, ChevronUp, KeyRound,
  FlaskConical, Upload, FileText, X, ChevronRight, CheckCheck,
  Activity, Link2, Unlink, ShieldAlert,
} from "lucide-react";


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;

// ── Export helper (client-side, no backend needed) ─────────────────────────
async function buildExportPayload(uid, profile) {
  const measSnap = await getDocs(collection(db, "users", uid, "measurements"));
  const measurements = measSnap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      method: data.method,
      source: data.source,
      fields: data.fields,
      note: data.note || null,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || null,
      importedAt: data.importedAt?.toDate?.()?.toISOString?.() || null,
    };
  });

  return {
    exportedAt: new Date().toISOString(),
    version: "1.0",
    profile: {
      nombres: profile.nombres,
      apellidos: profile.apellidos,
      fechaNacimiento: profile.fechaNacimiento,
      sexo: profile.sexo,
      email: profile.email,
      whatsapp: profile.whatsapp,
      residencia: profile.residencia,
      estadoCivil: profile.estadoCivil,
      nivelEducativo: profile.nivelEducativo,
      profesion: profile.profesion,
      ocupacion: profile.ocupacion,
      createdAt: profile.createdAt,
      completedAt: profile.completedAt,
    },
    vitals: {
      peso: profile.peso,
      talla: profile.talla,
      cintura: profile.cintura,
      paSistolica: profile.paSistolica,
      paDiastolica: profile.paDiastolica,
      glucemia: profile.glucemia,
      oximetria: profile.oximetria,
      imc: profile.imc || null,
    },
    forms: {
      f2: profile.f2Results || null,
      f3: profile.f3Results || null,
      f4: profile.f4Results || null,
      f5: profile.f5Results || null,
    },
    measurements,
    conditions: profile.condicionIds || [],
    medications: profile.medicamentos || [],
  };
}

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Section wrapper ─────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4">
      <div className="flex items-center gap-2 px-5 py-4 border-b" style={{ borderColor: "#F1F5F9" }}>
        <Icon size={16} style={{ color: "#00628F" }} />
        <h2 className="font-semibold text-sm" style={{ color: "#1E293B" }}>{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function MiCuenta() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    profile, setProfileAndSave, clientId, personalEmail, personalEmailVerified,
    updatePersonalEmail, requestDeletion,
    isImpersonating, isReadOnly, impersonatedUser,
  } = useUserProfile();

  const effectiveEmail = isImpersonating
    ? (impersonatedUser?.email || profile?.email || "")
    : (profile?.email || user?.email || "");

  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [verifyBlocked, setVerifyBlocked] = useState(null); // null | {until, seconds}
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [verifyError, setVerifyError] = useState(null);

  // Info edit state
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoSaved, setInfoSaved] = useState(false);

  // Company info
  const [company, setCompany] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    getDoc(doc(db, "clients", clientId))
      .then((snap) => { if (snap.exists()) setCompany({ id: snap.id, ...snap.data() }); })
      .catch(() => {});
  }, [clientId]);

  // Email personal state
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);

  // Email change (login/contact) — stores pendingEmail until P3 verification
  const [pendingEmail, setPendingEmail] = useState(null);
  const [editingEmailChange, setEditingEmailChange] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [newEmailError, setNewEmailError] = useState(null);
  const [savingEmailChange, setSavingEmailChange] = useState(false);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  // Exámenes clínicos state
  const [exams, setExams] = useState([]);
  const [examsLoaded, setExamsLoaded] = useState(false);
  const [uploadingExam, setUploadingExam] = useState(false);
  const [examPreview, setExamPreview] = useState(null);  // { filename, labDate, labName, results[] }
  const [examError, setExamError] = useState(null);
  const [expandedExam, setExpandedExam] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (snap.exists()) setPendingEmail(snap.data().pendingEmail || null);
      })
      .catch(() => {});
  }, [user?.uid]);

  // ── Google Health (integración de wearables) ──
  const API = BACKEND_URL;
  const [ghStatus, setGhStatus] = useState(null);   // null=cargando, {connected,...}
  const [ghBusy, setGhBusy] = useState(false);
  const [ghMsg, setGhMsg] = useState(null);          // {type:'ok'|'error', text}

  const loadGhStatus = useCallback(() => {
    if (!user?.uid) return;
    fetch(`${API}/api/integrations/google-health/status?uid=${encodeURIComponent(user.uid)}`)
      .then((r) => r.json())
      .then(setGhStatus)
      .catch(() => setGhStatus({ connected: false }));
  }, [API, user?.uid]);

  useEffect(() => { loadGhStatus(); }, [loadGhStatus]);

  // Maneja el retorno del flujo OAuth (?gh=connected | error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gh = params.get("gh");
    if (!gh) return;
    if (gh === "connected") setGhMsg({ type: "ok", text: "¡Google Health conectado correctamente!" });
    else setGhMsg({ type: "error", text: `No se pudo conectar (${params.get("reason") || "error"}).` });
    window.history.replaceState({}, "", window.location.pathname);
    loadGhStatus();
  }, [loadGhStatus]);

  const handleConnectGoogleHealth = () => {
    if (!user?.uid) return;
    const url = `${API}/api/integrations/google-health/connect?uid=${encodeURIComponent(user.uid)}&origin=${encodeURIComponent(window.location.origin)}`;
    window.location.href = url;
  };

  const handleDisconnectGoogleHealth = async () => {
    if (!user?.uid) return;
    setGhBusy(true);
    try {
      await fetch(`${API}/api/integrations/google-health/disconnect?uid=${encodeURIComponent(user.uid)}`, { method: "DELETE" });
      setGhMsg({ type: "ok", text: "Google Health desconectado." });
      setGhPreview(null);
      loadGhStatus();
    } catch { setGhMsg({ type: "error", text: "No se pudo desconectar." }); }
    finally { setGhBusy(false); }
  };

  const [ghPreview, setGhPreview] = useState(null);
  const [ghPreviewBusy, setGhPreviewBusy] = useState(false);
  const handlePreviewGoogleHealth = async () => {
    if (!user?.uid) return;
    setGhPreviewBusy(true); setGhPreview(null);
    try {
      const r = await fetch(`${API}/api/integrations/google-health/preview?uid=${encodeURIComponent(user.uid)}`);
      const data = await r.json();
      setGhPreview(data);
    } catch (e) {
      setGhPreview({ error: String(e) });
    } finally { setGhPreviewBusy(false); }
  };

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  // Delete state
  const [deleteStep, setDeleteStep] = useState(0); // 0=idle, 1=info, 2=confirm
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  // TODO section state
  const [showTodos, setShowTodos] = useState(false);

  const isB2B = !!clientId;
  const hasPersonalEmail = !!personalEmail;
  const isAtRisk = isB2B && !hasPersonalEmail;
  const { clientRole } = useUserProfile();
  const isCorpAdmin = isB2B && clientRole === "admin";

  // Auth methods
  const providers = user?.providerData || [];
  const hasGoogle = providers.some((p) => p.providerId === "google.com");
  const hasPassword = providers.some((p) => p.providerId === "password");
  // B2B email change: new email must match same corporate domain
  const corpDomain = isB2B && effectiveEmail ? effectiveEmail.split("@")[1] : null;

  async function handleSaveInfo() {
    if (!user?.uid) return;
    setSavingInfo(true);
    try {
      const fields = ["nombres", "apellidos", "fechaNacimiento", "sexo", "residencia", "profesion", "ocupacion", "estadoCivil", "whatsapp"];
      const updatedProfile = {
        ...profile,
        email: effectiveEmail || profile?.email || user?.email || "",
      };
      fields.forEach((k) => {
        if (infoForm[k] !== undefined) updatedProfile[k] = infoForm[k];
      });

      // 1. Update in-memory React state immediately AND persist normalized FHIR profile to Firestore
      await setProfileAndSave(updatedProfile);

      // 2. Keep root-level whatsapp & displayName synchronized if edited
      const rootUpdates = {};
      if (infoForm.whatsapp !== undefined) rootUpdates.whatsapp = infoForm.whatsapp;
      const fullName = [updatedProfile.nombres, updatedProfile.apellidos].filter(Boolean).join(" ").trim();
      if (fullName) rootUpdates.displayName = fullName;
      if (Object.keys(rootUpdates).length > 0) {
        await updateDoc(doc(db, "users", user.uid), rootUpdates).catch(() => {});
      }

      setEditingInfo(false);
      setInfoSaved(true);
      setTimeout(() => setInfoSaved(false), 3000);

      // 3. Synchronize demographic changes to GCP FHIR Store (and track in Outbox if offline)
      const targetEmail = effectiveEmail || user?.email;
      if (targetEmail) {
        syncF1(updatedProfile, targetEmail).catch((err) =>
          console.warn("[MiCuenta] Error sincronizando datos demográficos a FHIR:", err)
        );
      }
    } catch {
      // silently fail, user can retry
    } finally {
      setSavingInfo(false);
    }
  }

  async function handleEmailChange() {
    const val = newEmailInput.trim().toLowerCase();
    if (!EMAIL_RE.test(val)) { setNewEmailError("Email no válido"); return; }
    if (val === (user?.email || "").toLowerCase()) {
      setNewEmailError("El nuevo email debe ser diferente al actual");
      return;
    }
    // B2B: si tiene email+contraseña, validar que el nuevo sea del mismo dominio corporativo
    if (isB2B && hasPassword && corpDomain) {
      const newDomain = val.split("@")[1];
      if (newDomain !== corpDomain) {
        setNewEmailError(`Email corporativo debe ser del dominio @${corpDomain}`);
        return;
      }
    }
    setSavingEmailChange(true);
    setNewEmailError(null);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        pendingEmail: val,
        pendingEmailRequestedAt: serverTimestamp(),
      });
      setPendingEmail(val);
      setEditingEmailChange(false);
    } catch {
      setNewEmailError("Error al guardar. Intenta de nuevo.");
    } finally {
      setSavingEmailChange(false);
    }
  }

  async function cancelEmailChange() {
    await updateDoc(doc(db, "users", user.uid), {
      pendingEmail: null,
      pendingEmailRequestedAt: null,
    });
    setPendingEmail(null);
  }

  async function handleSaveEmail() {
    const val = emailInput.trim().toLowerCase();
    if (!val) {
      await updatePersonalEmail(null);
      setEditingEmail(false);
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 3000);
      return;
    }
    if (!EMAIL_RE.test(val)) {
      setEmailError("Email inválido");
      return;
    }
    setEmailError(null);
    setSavingEmail(true);
    try {
      await updatePersonalEmail(val);
      setEditingEmail(false);
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 3000);
    } catch {
      setEmailError("Error al guardar. Intenta de nuevo.");
    } finally {
      setSavingEmail(false);
    }
  }

  async function handleExport() {
    if (!user?.uid) return;
    setExporting(true);
    try {
      const data = await buildExportPayload(user.uid, profile);
      const name = [profile.nombres, profile.apellidos].filter(Boolean).join("-") || "perfil";
      downloadJson(data, `we360-${name.toLowerCase().replace(/\s+/g, "-")}.json`);
      setExportDone(true);
      setTimeout(() => setExportDone(false), 4000);
    } catch {
      // silently fail — user can retry
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (deleteInput !== "ELIMINAR") return;
    setDeleting(true);
    try {
      await requestDeletion();
      await logout();
      navigate("/");
    } catch {
      setDeleting(false);
    }
  }

  // ── Exámenes clínicos ────────────────────────────────────────────────────
  async function loadExams() {
    if (!user?.uid || examsLoaded) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/exams/${user.uid}`);
      const data = await res.json();
      if (data.ok) setExams(data.exams || []);
    } catch { /* silent */ }
    setExamsLoaded(true);
  }

  async function handleExamUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) return;
    e.target.value = "";

    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setExamError("Formato no soportado. Usa PDF, JPG, PNG o WEBP.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setExamError("El archivo supera 10 MB.");
      return;
    }

    setUploadingExam(true);
    setExamError(null);
    setExamPreview(null);

    try {
      const form = new FormData();
      form.append("uid", user.uid);
      form.append("file", file);

      const res = await fetch(`${BACKEND_URL}/api/exams/upload`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        setExamError(data.detail || data.error || "Error procesando el examen.");
        return;
      }

      setExamPreview({
        exam_id:   data.exam_id,
        filename:  data.filename,
        labDate:   data.labDate,
        labName:   data.labName,
        results:   data.results || [],
        method:    data.method,
      });

      // Agregar al historial
      setExams((prev) => [{
        exam_id:        data.exam_id,
        filename:       data.filename,
        uploaded_at:    new Date().toISOString(),
        parsing_status: "completed",
        lab_date:       data.labDate,
        lab_name:       data.labName,
        results:        data.results || [],
      }, ...prev]);

    } catch { setExamError("No se pudo conectar con el servidor."); }
    finally  { setUploadingExam(false); }
  }

  async function handleDeleteExam(examId) {
    if (!user?.uid) return;
    try {
      await fetch(`${BACKEND_URL}/api/exams/${user.uid}/${examId}`, { method: "DELETE" });
      setExams((prev) => prev.filter((e) => e.exam_id !== examId));
      if (examPreview?.exam_id === examId) setExamPreview(null);
    } catch { /* silent */ }
  }

  async function handleRequestVerification() {    if (!personalEmail || !user?.uid) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/request-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: user.uid,
          email: personalEmail,
          nombre: [profile.nombres, profile.apellidos].filter(Boolean).join(" ") || null,
        }),
      });
      const data = await res.json();

      if (data.blocked) {
        setVerifyBlocked({
          until: data.blocked_until,
          seconds: data.retry_after_seconds || 900,
        });
        setVerifySent(false);
      } else if (data.ok) {
        setVerifySent(true);
        setVerifyAttempts(data.attempt_count || 1);
        setVerifyBlocked(null);
      } else {
        setVerifyError(data.message || "Error al enviar el correo de verificación.");
      }
    } catch {
      setVerifyError("No se pudo conectar con el servidor. Intenta de nuevo.");
    } finally {
      setVerifying(false);
    }
  }

  async function handleChangePassword() {    const { current, next, confirm } = pwForm;
    if (!current) { setPwError("Ingresa tu contraseña actual"); return; }
    if (next.length < 8) { setPwError("La nueva contraseña debe tener al menos 8 caracteres"); return; }
    if (next !== confirm) { setPwError("Las contraseñas no coinciden"); return; }
    setPwError(null);
    setPwSaving(true);
    try {
      // Re-authenticate before sensitive operation
      const credential = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, next);

      // Send security email notification
      if (BACKEND_URL) {
        const now = new Date().toLocaleDateString("es-PA", { year: "numeric", month: "long", day: "numeric" });
        fetch(`${BACKEND_URL}/api/email/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            template: "password_changed",
            to_email: user.email,
            data: {
              nombre: [profile.nombres, profile.apellidos].filter(Boolean).join(" ") || null,
              fecha: now,
            },
          }),
        }).catch(() => {});
      }

      setPwDone(true);
      setPwForm({ current: "", next: "", confirm: "" });
      setShowChangePassword(false);
      setTimeout(() => setPwDone(false), 5000);
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPwError("Contraseña actual incorrecta");
      } else if (err.code === "auth/weak-password") {
        setPwError("Contraseña muy débil. Usa al menos 8 caracteres con letras y números");
      } else if (err.code === "auth/requires-recent-login") {
        setPwError("Por seguridad, cierra sesión, vuelve a ingresar y reintenta");
      } else {
        setPwError("Error al cambiar la contraseña. Intenta de nuevo.");
      }
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: "#FCFAEE" }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm mb-6 hover:opacity-75 transition-opacity"
          style={{ color: "#64748B", background: "none", border: "none", cursor: "pointer" }}
          data-testid="mi-cuenta-back"
        >
          <ArrowLeft size={16} />
          Volver al Dashboard
        </button>

        <h1 className="text-2xl font-bold mb-6" style={{ color: "#1E293B" }}>
          Mi cuenta
        </h1>

        {/* ── Tarjeta corporativa — PROMINENTE al inicio ── */}
        {isB2B && company && (
          <div
            className="rounded-2xl mb-5 overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1D4ED8 0%, #0284C7 100%)" }}
            data-testid="mi-cuenta-company-card"
          >
            <div className="px-5 py-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Building2 size={22} style={{ color: "white" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Cuenta corporativa activa
                </p>
                <p className="text-xl font-bold leading-tight" style={{ color: "white" }}>
                  {company.name}
                </p>
                {company.category && (
                  <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.65)" }}>
                    {company.category} · {isCorpAdmin ? "Administrador" : "Empleado"}
                  </p>
                )}
              </div>
              {/* Solo admins ven el link a la consola */}
              {isCorpAdmin && (
                <button
                  onClick={() => navigate("/cliente")}
                  className="shrink-0 px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all hover:bg-white/30 active:scale-95"
                  style={{ background: "rgba(255,255,255,0.18)", color: "white", border: "1px solid rgba(255,255,255,0.3)", cursor: "pointer" }}
                  data-testid="mi-cuenta-consola-link"
                >
                  Ver consola →
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Alerta de riesgo ── */}
        {isAtRisk && (
          <div
            className="flex items-start gap-3 px-4 py-4 rounded-xl mb-4"
            style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
            data-testid="mi-cuenta-risk-banner"
          >
            <AlertTriangle size={18} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: "#92400E" }}>
                Tu acceso depende de {company?.name || "tu cuenta corporativa"}
              </p>
              <p className="text-xs mt-1" style={{ color: "#B45309" }}>
                Si la empresa cancela tu acceso Google, podrías perder el acceso a todos tus datos de salud.
                Agrega un email personal como respaldo ahora.
              </p>
              <button
                onClick={() => { setEditingEmail(true); setEmailInput(""); }}
                className="mt-2 text-xs font-semibold underline"
                style={{ color: "#D97706", background: "none", border: "none", cursor: "pointer" }}
              >
                Agregar email personal →
              </button>
            </div>
          </div>
        )}

        {/* ── 1. Mi información ── */}
        <Section title="Mi información" icon={User}>
          {infoSaved && (
            <div className="flex items-center gap-2 text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: "#F0FDF4", color: "#166534" }}>
              <CheckCircle size={13} /> Información actualizada correctamente
            </div>
          )}

          {!editingInfo ? (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mb-4">
                {[
                  ["Nombre", [profile.nombres, profile.apellidos].filter(Boolean).join(" ") || impersonatedUser?.name || "—"],
                  ["Email", effectiveEmail || "—"],
                  ["Fecha de nacimiento", profile.fechaNacimiento || "—"],
                  ["Sexo", profile.sexo || "—"],
                  ["Residencia", profile.residencia || "—"],
                  ["Profesión", profile.profesion || "—"],
                  ["Ocupación", profile.ocupacion || "—"],
                  ["WhatsApp", profile.whatsapp || "—"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: "#94A3B8" }}>
                      {label}
                    </p>
                    <p style={{ color: "#1E293B" }}>{value}</p>
                  </div>
                ))}
              </div>
              {!isReadOnly && (
                <button
                  onClick={() => {
                    setInfoForm({
                      nombres: profile.nombres || "",
                      apellidos: profile.apellidos || "",
                      fechaNacimiento: profile.fechaNacimiento || "",
                      sexo: profile.sexo || "",
                      residencia: profile.residencia || "",
                      profesion: profile.profesion || "",
                      ocupacion: profile.ocupacion || "",
                      estadoCivil: profile.estadoCivil || "",
                      whatsapp: profile.whatsapp || "",
                    });
                    setEditingInfo(true);
                  }}
                  className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl transition-all"
                  style={{ background: "#EFF6FF", color: "#0284C7", border: "1px solid #BFDBFE" }}
                  data-testid="mi-cuenta-edit-info-btn"
                >
                  <Pencil size={13} /> Editar información
                </button>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "nombres", label: "Nombres", type: "text" },
                  { key: "apellidos", label: "Apellidos", type: "text" },
                  { key: "fechaNacimiento", label: "Fecha de nacimiento", type: "date" },
                  { key: "whatsapp", label: "WhatsApp", type: "tel" },
                  { key: "residencia", label: "Residencia", type: "text" },
                  { key: "profesion", label: "Profesión", type: "text" },
                  { key: "ocupacion", label: "Ocupación", type: "text" },
                ].map(({ key, label, type }) => (
                  <div key={key}>
                    <label className="text-[11px] font-semibold uppercase tracking-wide block mb-1" style={{ color: "#94A3B8" }}>
                      {label}
                    </label>
                    <input
                      type={type}
                      value={infoForm[key] || ""}
                      onChange={(e) => setInfoForm((p) => ({ ...p, [key]: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
                      data-testid={`mi-cuenta-${key}-input`}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide block mb-1" style={{ color: "#94A3B8" }}>
                    Sexo biológico (al nacer)
                  </label>
                  <select
                    value={infoForm.sexo || ""}
                    onChange={(e) => setInfoForm((p) => ({ ...p, sexo: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ borderColor: "#E2E8F0", color: "#1E293B" }}
                    data-testid="mi-cuenta-sexo-input"
                  >
                    <option value="">Seleccionar</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                  </select>
                </div>
              </div>
              <p className="text-[11px]" style={{ color: "#94A3B8" }}>
                Nota: El email de acceso solo se puede cambiar desde la configuración de tu cuenta Google.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setEditingInfo(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm border"
                  style={{ borderColor: "#E2E8F0", color: "#64748B" }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveInfo}
                  disabled={savingInfo}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{ background: "#00628F", color: "white", border: "none", cursor: savingInfo ? "wait" : "pointer" }}
                  data-testid="mi-cuenta-save-info-btn"
                >
                  {savingInfo ? <Loader2 size={13} className="animate-spin" /> : null}
                  Guardar cambios
                </button>
              </div>
            </div>
          )}
        </Section>

        {/* ── Autorización médica (PAR-Q) ── */}
        {profile.f2Results?.parqAlerta && (
          <Section title="Autorización médica (PAR-Q)" icon={FileText}>
            {profile.parqConsentimiento === "signed" ? (
              <div className="rounded-xl p-4" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} data-testid="mi-cuenta-parq-signed">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} style={{ color: "#16A34A" }} />
                  <p className="text-sm font-bold" style={{ color: "#166534" }}>Consentimiento médico subido</p>
                </div>
                <p className="text-xs mb-3" style={{ color: "#15803D" }}>
                  Registraste tu autorización médica firmada
                  {profile.parqConsentimientoAt ? ` el ${new Date(profile.parqConsentimientoAt).toLocaleDateString("es-PA")}` : ""}.
                </p>
                {profile.parqConsentimientoUrl && (
                  <a
                    href={profile.parqConsentimientoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
                    style={{ background: "#16A34A", color: "white" }}
                    data-testid="mi-cuenta-parq-view-doc"
                  >
                    <FileText size={13} /> Ver documento
                  </a>
                )}
              </div>
            ) : profile.parqConsentimiento === "own_risk" ? (
              <div className="rounded-xl p-4" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid="mi-cuenta-parq-ownrisk">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} style={{ color: "#D97706" }} />
                  <p className="text-sm font-bold" style={{ color: "#92400E" }}>Continuaste bajo tu propio riesgo</p>
                </div>
                <p className="text-xs" style={{ color: "#78350F" }}>
                  Aceptaste continuar sin autorización médica
                  {profile.parqConsentimientoAt ? ` el ${new Date(profile.parqConsentimientoAt).toLocaleDateString("es-PA")}` : ""}.
                  Reconociste que la plataforma no se hace responsable de tu estado de salud sin supervisión clínica.
                </p>
              </div>
            ) : (
              <div className="rounded-xl p-4" style={{ background: "#FEF3C7", border: "1px solid #FCD34D" }} data-testid="mi-cuenta-parq-pending">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} style={{ color: "#D97706" }} />
                  <p className="text-sm font-bold" style={{ color: "#92400E" }}>Autorización pendiente</p>
                </div>
                <p className="text-xs mb-3" style={{ color: "#78350F" }}>
                  Tu evaluación física detectó {profile.f2Results.parqRiskCount} {profile.f2Results.parqRiskCount === 1 ? "factor de riesgo" : "factores de riesgo"}. Sube tu autorización médica o decide cómo continuar.
                </p>
                <button
                  onClick={() => navigate("/medicion/nueva")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white"
                  style={{ background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)", border: "none", cursor: "pointer" }}
                  data-testid="mi-cuenta-parq-resolve"
                >
                  <Upload size={13} /> Resolver autorización
                </button>
              </div>
            )}
          </Section>
        )}

        {/* ── Datos de salud conectados (Google Health) ── */}
        <Section title="Datos de salud conectados" icon={Activity}>
          {ghMsg && (
            <div
              className="rounded-lg px-3 py-2 mb-3 text-xs font-semibold"
              style={ghMsg.type === "ok"
                ? { background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0" }
                : { background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }}
              data-testid="gh-message"
            >
              {ghMsg.text}
            </div>
          )}
          <div className="rounded-xl p-4" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                   style={{ background: "linear-gradient(135deg,#00628F,#2A9D8F)" }}>
                <Activity size={20} color="#fff" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ color: "#0F172A" }}>Google Health</p>
                <p className="text-xs mb-3" style={{ color: "#64748B" }}>
                  Conecta tu cuenta Google para traer tus datos de Fitbit, Pixel Watch o Wear OS
                  (pasos, frecuencia cardíaca, sueño y más) y enriquecer tu perfil de salud.
                </p>

                {ghStatus === null ? (
                  <div className="flex items-center gap-2 text-xs" style={{ color: "#94A3B8" }}>
                    <Loader2 size={14} className="animate-spin" /> Verificando conexión…
                  </div>
                ) : ghStatus.connected ? (
                  <div data-testid="gh-connected-block">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={15} style={{ color: "#16A34A" }} />
                      <span className="text-xs font-bold" style={{ color: "#166534" }}>Conectado</span>
                    </div>
                    {ghStatus.connected_at && (
                      <p className="text-[11px] mb-3" style={{ color: "#64748B" }}>
                        Desde el {new Date(ghStatus.connected_at).toLocaleDateString("es-PA")}
                      </p>
                    )}
                    <button
                      onClick={handleDisconnectGoogleHealth}
                      disabled={ghBusy}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border-2 disabled:opacity-50"
                      style={{ borderColor: "#FECACA", color: "#B91C1C", cursor: "pointer" }}
                      data-testid="gh-disconnect-btn"
                    >
                      {ghBusy ? <Loader2 size={13} className="animate-spin" /> : <Unlink size={13} />} Desconectar
                    </button>
                    <button
                      onClick={handlePreviewGoogleHealth}
                      disabled={ghPreviewBusy}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg ml-2 disabled:opacity-50"
                      style={{ background: "#0F172A", color: "#fff", border: "none", cursor: "pointer" }}
                      data-testid="gh-preview-btn"
                    >
                      {ghPreviewBusy ? <Loader2 size={13} className="animate-spin" /> : <Activity size={13} />} Probar datos
                    </button>

                    {/* Consola de datos crudos */}
                    {ghPreview && (
                      <div className="mt-3" data-testid="gh-preview-console">
                        {Array.isArray(ghPreview.results) && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {ghPreview.results.map((r) => (
                              <span key={r.dataType}
                                className="text-[10px] font-bold px-2 py-1 rounded"
                                style={r.ok
                                  ? { background: "#DCFCE7", color: "#166534" }
                                  : { background: "#FEE2E2", color: "#B91C1C" }}>
                                {r.dataType}: {r.ok ? `${r.count} pts` : `HTTP ${r.status}`}
                              </span>
                            ))}
                          </div>
                        )}
                        <pre
                          className="text-[10.5px] leading-relaxed p-3 rounded-lg overflow-auto"
                          style={{ background: "#0B1220", color: "#7EE787", maxHeight: "320px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        >
{JSON.stringify(ghPreview, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div data-testid="gh-disconnected-block">
                    {ghStatus.configured === false && (
                      <p className="text-[11px] mb-2" style={{ color: "#D97706" }}>
                        ⚠️ Integración aún no configurada por el administrador.
                      </p>
                    )}
                    <button
                      onClick={handleConnectGoogleHealth}
                      disabled={ghStatus.configured === false}
                      className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg text-white disabled:opacity-50"
                      style={{ background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)", border: "none", cursor: "pointer" }}
                      data-testid="gh-connect-btn"
                    >
                      <Link2 size={14} /> Conectar Google Health
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>

        {/* ── 2. Seguridad y acceso ── */}
        <Section title="Seguridad y acceso" icon={Shield}>
          {/* Auth methods */}
          <div className="space-y-2 mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#94A3B8" }}>
              Métodos de acceso activos
            </p>
            {hasGoogle && (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold" style={{ color: "#1A73E8" }}>G</span>
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: "#1E293B" }}>Google</p>
                  <p className="text-[11px]" style={{ color: "#64748B" }}>{effectiveEmail}</p>
                </div>
                {isB2B && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                    Corporativo
                  </span>
                )}
              </div>
            )}
            {hasPassword && (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                  <Lock size={12} style={{ color: "#64748B" }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: "#1E293B" }}>Email y contraseña</p>
                  <p className="text-[11px]" style={{ color: "#64748B" }}>{effectiveEmail}</p>
                </div>
              </div>
            )}
          </div>

          {/* Change password (only for email+password users) */}
          {hasPassword && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "#F1F5F9" }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#94A3B8" }}>
                  Contraseña
                </p>
                {!showChangePassword && !isReadOnly && (
                  <button
                    onClick={() => { setShowChangePassword(true); setPwForm({ current: "", next: "", confirm: "" }); setPwError(null); }}
                    className="text-xs font-semibold"
                    style={{ color: "#00628F", background: "none", border: "none", cursor: "pointer" }}
                    data-testid="mi-cuenta-change-password-btn"
                  >
                    Cambiar
                  </button>
                )}
              </div>

              {pwDone && (
                <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-2" style={{ background: "#F0FDF4", color: "#166534" }}>
                  <CheckCircle size={13} /> Contraseña actualizada. Te enviamos un correo de confirmación.
                </div>
              )}

              {!showChangePassword ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                  <KeyRound size={14} style={{ color: "#94A3B8" }} />
                  <span className="text-sm" style={{ color: "#64748B" }}>••••••••</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {[
                    { key: "current", label: "Contraseña actual", testid: "mi-cuenta-pw-current" },
                    { key: "next",    label: "Nueva contraseña",  testid: "mi-cuenta-pw-new" },
                    { key: "confirm", label: "Confirmar nueva",   testid: "mi-cuenta-pw-confirm" },
                  ].map(({ key, label, testid }) => (
                    <div key={key}>
                      <label className="text-[11px] font-semibold uppercase tracking-wide block mb-1" style={{ color: "#94A3B8" }}>
                        {label}
                      </label>
                      <input
                        type="password"
                        value={pwForm[key]}
                        onChange={(e) => { setPwForm((p) => ({ ...p, [key]: e.target.value })); setPwError(null); }}
                        autoComplete={key === "current" ? "current-password" : "new-password"}
                        className="w-full px-3 py-2.5 rounded-xl border text-sm"
                        style={{ borderColor: pwError ? "#FCA5A5" : "#E2E8F0", color: "#1E293B" }}
                        data-testid={testid}
                      />
                    </div>
                  ))}
                  {pwError && (
                    <p className="text-xs" style={{ color: "#DC2626" }} data-testid="mi-cuenta-pw-error">{pwError}</p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => { setShowChangePassword(false); setPwError(null); }}
                      className="flex-1 py-2.5 rounded-xl text-sm border"
                      style={{ borderColor: "#E2E8F0", color: "#64748B" }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleChangePassword}
                      disabled={pwSaving}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                      style={{ background: "#00628F", color: "white", border: "none", cursor: pwSaving ? "wait" : "pointer" }}
                      data-testid="mi-cuenta-pw-save-btn"
                    >
                      {pwSaving ? <Loader2 size={13} className="animate-spin" /> : null}
                      Actualizar contraseña
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email change (B2C / B2B with password) */}
          {(!isB2B || hasPassword) && (
            <div className="mt-5 pt-5 border-t" style={{ borderColor: "#F1F5F9" }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#94A3B8" }}>
                  Correo electrónico de acceso
                </p>
                {!editingEmailChange && !pendingEmail && !isReadOnly && (
                  <button
                    onClick={() => { setEditingEmailChange(true); setNewEmailInput(""); setNewEmailError(null); }}
                    className="text-xs font-semibold"
                    style={{ color: "#00628F", background: "none", border: "none", cursor: "pointer" }}
                    data-testid="mi-cuenta-change-email-btn"
                  >
                    Cambiar
                  </button>
                )}
              </div>

              {/* B2B solo Google: explicación */}
              {isB2B && !hasPassword && (
                <div className="px-3 py-2.5 rounded-xl text-xs" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", color: "#64748B" }}>
                  <Lock size={12} className="inline mr-1.5" style={{ color: "#94A3B8" }} />
                  Tu email de acceso (<strong>{effectiveEmail}</strong>) está gestionado por tu empleador a través de Google Workspace. No se puede modificar desde WE360.
                  Puedes agregar un email personal de respaldo arriba.
                </div>
              )}

              {/* Pending email badge */}
              {pendingEmail && !editingEmailChange && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}>
                  <Clock size={13} style={{ color: "#D97706", flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: "#92400E" }}>Cambio pendiente de verificación</p>
                    <p className="text-[11px] truncate" style={{ color: "#B45309" }}>
                      Nuevo: <strong>{pendingEmail}</strong>
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: "#B45309" }}>
                      Se activará cuando se habilite el servicio de email (próximamente)
                    </p>
                  </div>
                  <button
                    onClick={cancelEmailChange}
                    className="text-[11px] font-semibold shrink-0"
                    style={{ color: "#D97706", background: "none", border: "none", cursor: "pointer" }}
                    data-testid="mi-cuenta-cancel-email-change"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {/* Edit form */}
              {(!isB2B || hasPassword) && editingEmailChange && (
                <div>
                  <p className="text-[11px] mb-2" style={{ color: "#64748B" }}>
                    Email actual: <strong>{user?.email}</strong>
                    {isB2B && corpDomain && (
                      <span className="ml-1" style={{ color: "#D97706" }}>
                        (solo emails @{corpDomain})
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={newEmailInput}
                      onChange={(e) => { setNewEmailInput(e.target.value); setNewEmailError(null); }}
                      placeholder="nuevo@correo.com"
                      autoFocus
                      className="flex-1 px-3 py-2.5 rounded-xl border text-sm"
                      style={{ borderColor: newEmailError ? "#FCA5A5" : "#E2E8F0", color: "#1E293B" }}
                      data-testid="mi-cuenta-new-email-input"
                    />
                    <button
                      onClick={handleEmailChange}
                      disabled={savingEmailChange}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-1"
                      style={{ background: "#00628F", color: "white", border: "none", cursor: savingEmailChange ? "wait" : "pointer" }}
                      data-testid="mi-cuenta-save-email-change-btn"
                    >
                      {savingEmailChange ? <Loader2 size={13} className="animate-spin" /> : "Guardar"}
                    </button>
                    <button
                      onClick={() => { setEditingEmailChange(false); setNewEmailError(null); }}
                      className="px-3 py-2.5 rounded-xl text-sm"
                      style={{ background: "#F1F5F9", color: "#64748B", border: "none", cursor: "pointer" }}
                    >
                      Cancelar
                    </button>
                  </div>
                  {newEmailError && (
                    <p className="text-xs mt-1" style={{ color: "#DC2626" }}>{newEmailError}</p>
                  )}
                  <p className="text-[11px] mt-1.5" style={{ color: "#94A3B8" }}>
                    Guardar un nuevo email invalidará la verificación actual. Deberás verificarlo de nuevo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Personal email */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#94A3B8" }}>
                Email personal de respaldo
              </p>
              {!editingEmail && (
                <button
                  onClick={() => { setEditingEmail(true); setEmailInput(personalEmail || ""); setEmailError(null); setVerifySent(false); setVerifyBlocked(null); }}
                  className="text-xs font-semibold"
                  style={{ color: "#00628F", background: "none", border: "none", cursor: "pointer" }}
                  data-testid="mi-cuenta-edit-email-btn"
                >
                  {personalEmail ? "Cambiar" : "+ Agregar"}
                </button>
              )}
            </div>

            {!editingEmail ? (
              <>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                  style={{
                    background: personalEmail ? (personalEmailVerified ? "#F0FDF4" : "#FFFBEB") : "#F8FAFC",
                    border: `1px solid ${personalEmail ? (personalEmailVerified ? "#BBF7D0" : "#FCD34D") : "#E2E8F0"}`,
                  }}
                  data-testid="mi-cuenta-personal-email-display"
                >
                  <Mail size={14} style={{ color: personalEmail ? (personalEmailVerified ? "#16A34A" : "#D97706") : "#94A3B8" }} />
                  <span className="text-sm flex-1" style={{ color: personalEmail ? "#1E293B" : "#94A3B8" }}>
                    {personalEmail || "Sin email de respaldo configurado"}
                  </span>
                  {personalEmail && personalEmailVerified && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#16A34A" }}
                      data-testid="mi-cuenta-email-verified-badge">
                      <CheckCircle size={11} /> Verificado
                    </span>
                  )}
                  {personalEmail && !personalEmailVerified && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#D97706" }}
                      data-testid="mi-cuenta-email-unverified-badge">
                      Sin verificar
                    </span>
                  )}
                  {emailSaved && !personalEmail && (
                    <span className="text-[10px]" style={{ color: "#16A34A" }}>Eliminado</span>
                  )}
                </div>

                {/* Botón verificar + estados */}
                {personalEmail && !personalEmailVerified && (
                  <div className="mt-2">
                    {verifyBlocked ? (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl"
                        style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                        <AlertTriangle size={13} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <p className="text-xs font-semibold" style={{ color: "#991B1B" }}>
                            Función bloqueada temporalmente
                          </p>
                          <p className="text-[11px] mt-0.5" style={{ color: "#B91C1C" }}>
                            Superaste el límite de {3} intentos. Espera {Math.ceil(verifyBlocked.seconds / 60)} minutos para reintentar.
                          </p>
                        </div>
                      </div>
                    ) : verifySent ? (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl"
                        style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
                        data-testid="mi-cuenta-verify-sent-banner">
                        <CheckCircle size={13} style={{ color: "#16A34A", flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <p className="text-xs font-semibold" style={{ color: "#166534" }}>
                            Correo enviado ({verifyAttempts}/3 intentos usados)
                          </p>
                          <p className="text-[11px] mt-0.5" style={{ color: "#15803D" }}>
                            Revisa tu bandeja en <strong>{personalEmail}</strong> y haz clic en el enlace. Válido por 24h.
                          </p>
                          {verifyAttempts < 3 && (
                            <button
                              onClick={handleRequestVerification}
                              disabled={verifying}
                              className="text-[11px] font-semibold mt-1 underline"
                              style={{ color: "#15803D", background: "none", border: "none", cursor: "pointer" }}
                              data-testid="mi-cuenta-resend-btn"
                            >
                              Reenviar correo
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        {verifyError && (
                          <p className="text-xs mb-2" style={{ color: "#DC2626" }}
                            data-testid="mi-cuenta-verify-error">{verifyError}</p>
                        )}
                        <button
                          onClick={handleRequestVerification}
                          disabled={verifying}
                          className="flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl transition-all"
                          style={{ background: "#FFFBEB", color: "#D97706", border: "1px solid #FCD34D", cursor: verifying ? "wait" : "pointer" }}
                          data-testid="mi-cuenta-verify-btn"
                        >
                          {verifying ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                          Verificar este email
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(null); }}
                    placeholder="tucorreo@personal.com"
                    autoFocus
                    className="flex-1 px-3 py-2.5 rounded-xl border text-sm"
                    style={{ borderColor: emailError ? "#FCA5A5" : "#E2E8F0", color: "#1E293B" }}
                    data-testid="mi-cuenta-personal-email-input"
                  />
                  <button
                    onClick={handleSaveEmail}
                    disabled={savingEmail}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
                    style={{ background: "#00628F", color: "white", border: "none", cursor: savingEmail ? "wait" : "pointer" }}
                    data-testid="mi-cuenta-save-email-btn"
                  >
                    {savingEmail ? <Loader2 size={13} className="animate-spin" /> : "Guardar"}
                  </button>
                  <button
                    onClick={() => { setEditingEmail(false); setEmailError(null); }}
                    className="px-3 py-2.5 rounded-xl text-sm"
                    style={{ background: "#F1F5F9", color: "#64748B", border: "none", cursor: "pointer" }}
                  >
                    Cancelar
                  </button>
                </div>
                {emailError && (
                  <p className="text-xs mt-1" style={{ color: "#DC2626" }}>{emailError}</p>
                )}
                <p className="text-[11px] mt-1.5" style={{ color: "#94A3B8" }}>
                  Se guardará sin verificar por ahora. La verificación por email estará disponible próximamente.
                </p>
              </div>
            )}
          </div>

        </Section>

        {/* ── 3. Mis Exámenes ── */}
        <Section title="Mis Exámenes Clínicos" icon={FlaskConical}>
          <p className="text-sm mb-4" style={{ color: "#475569" }}>
            Sube tus resultados de laboratorio (PDF o imagen). El AI extrae los valores automáticamente y los guarda en tu historial.
          </p>

          {/* Upload button */}
          <label
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl cursor-pointer transition-all mb-4"
            style={{
              border: "2px dashed #CBD5E1",
              background: uploadingExam ? "#F8FAFC" : "white",
              cursor: uploadingExam ? "wait" : "pointer",
            }}
            onClick={() => { loadExams(); setExamError(null); }}
            data-testid="mi-cuenta-exam-upload-label"
          >
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleExamUpload}
              disabled={uploadingExam}
              data-testid="mi-cuenta-exam-file-input"
            />
            {uploadingExam ? (
              <>
                <Loader2 size={18} className="animate-spin" style={{ color: "#00628F" }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#00628F" }}>Analizando con AI...</p>
                  <p className="text-xs" style={{ color: "#94A3B8" }}>Esto puede tomar 15–30 segundos</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#EFF6FF" }}>
                  <Upload size={16} style={{ color: "#00628F" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1E293B" }}>Subir examen</p>
                  <p className="text-xs" style={{ color: "#94A3B8" }}>PDF, JPG, PNG — máx. 10 MB</p>
                </div>
              </>
            )}
          </label>

          {examError && (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-3"
              style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}
              data-testid="mi-cuenta-exam-error">
              <AlertTriangle size={13} /> {examError}
            </div>
          )}

          {/* Preview del último examen subido */}
          {examPreview && (
            <div className="mb-4 rounded-xl overflow-hidden"
              style={{ border: "1px solid #BBF7D0", background: "#F0FDF4" }}
              data-testid="mi-cuenta-exam-preview">
              <div className="flex items-center justify-between px-4 py-2.5"
                style={{ borderBottom: "1px solid #BBF7D0" }}>
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} style={{ color: "#16A34A" }} />
                  <span className="text-xs font-semibold" style={{ color: "#166534" }}>
                    {examPreview.results.length} resultados extraídos
                    {examPreview.labName && ` — ${examPreview.labName}`}
                    {examPreview.labDate && ` (${examPreview.labDate})`}
                  </span>
                </div>
                <button onClick={() => setExamPreview(null)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280" }}>
                  <X size={14} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#DCFCE7" }}>
                      {["Examen", "Valor", "Unidad", "Referencia", "Estado"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 font-semibold"
                          style={{ color: "#166534", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {examPreview.results.map((r, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #D1FAE5", background: i % 2 === 0 ? "white" : "#F0FDF4" }}>
                        <td className="px-3 py-2 font-medium" style={{ color: "#1E293B" }}>{r.testName}</td>
                        <td className="px-3 py-2 font-bold" style={{ color: r.status === "alto" ? "#DC2626" : r.status === "bajo" ? "#D97706" : "#1E293B" }}>
                          {r.value ?? "—"}
                        </td>
                        <td className="px-3 py-2" style={{ color: "#64748B" }}>{r.unit || "—"}</td>
                        <td className="px-3 py-2" style={{ color: "#64748B" }}>{r.referenceRange || "—"}</td>
                        <td className="px-3 py-2">
                          {r.status === "alto" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#FEE2E2", color: "#DC2626" }}>Alto</span>}
                          {r.status === "bajo" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#FEF3C7", color: "#D97706" }}>Bajo</span>}
                          {r.status === "normal" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#DCFCE7", color: "#16A34A" }}>Normal</span>}
                          {!r.status && <span style={{ color: "#CBD5E1" }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 bg-amber-50/70 border-t border-amber-200/60 flex items-start gap-2 text-[11px] text-amber-900 leading-relaxed">
                <ShieldAlert size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Aviso Legal y Clínico:</strong> Este análisis es de carácter informativo y preventivo, basado en los rangos de referencia estándar. No constituye un diagnóstico médico definitivo ni una prescripción terapéutica. Le sugerimos consultar con su Médico General para una valoración clínica personalizada.
                </span>
              </div>
            </div>
          )}

          {/* Historial de exámenes */}
          {exams.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#94A3B8" }}>
                Historial
              </p>
              <div className="space-y-1.5">
                {exams.map((exam) => (
                  <div key={exam.exam_id} className="rounded-xl overflow-hidden"
                    style={{ border: "1px solid #E2E8F0" }}>
                    <div
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                      style={{ background: expandedExam === exam.exam_id ? "#F8FAFC" : "white" }}
                      onClick={() => setExpandedExam(expandedExam === exam.exam_id ? null : exam.exam_id)}
                    >
                      <FileText size={14} style={{ color: "#64748B", flexShrink: 0 }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate" style={{ color: "#1E293B" }}>{exam.filename}</p>
                        <p className="text-[10px]" style={{ color: "#94A3B8" }}>
                          {exam.lab_date || new Date(exam.uploaded_at).toLocaleDateString("es-PA")}
                          {exam.lab_name && ` — ${exam.lab_name}`}
                          {" · "}{exam.results?.length || 0} resultados
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteExam(exam.exam_id); }}
                          className="p-1 rounded"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#CBD5E1" }}
                          title="Eliminar examen"
                          data-testid={`mi-cuenta-exam-delete-${exam.exam_id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                        {expandedExam === exam.exam_id
                          ? <ChevronDown size={14} style={{ color: "#94A3B8" }} />
                          : <ChevronRight size={14} style={{ color: "#94A3B8" }} />}
                      </div>
                    </div>
                    {expandedExam === exam.exam_id && exam.results?.length > 0 && (
                      <div className="overflow-x-auto border-t" style={{ borderColor: "#F1F5F9" }}>
                        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "#F8FAFC" }}>
                              {["Examen", "Valor", "Unidad", "Estado"].map((h) => (
                                <th key={h} className="text-left px-3 py-2 font-semibold"
                                  style={{ color: "#64748B" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {exam.results.map((r, i) => (
                              <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                                <td className="px-3 py-1.5" style={{ color: "#1E293B" }}>{r.testName}</td>
                                <td className="px-3 py-1.5 font-bold"
                                  style={{ color: r.status === "alto" ? "#DC2626" : r.status === "bajo" ? "#D97706" : "#1E293B" }}>
                                  {r.value ?? "—"}
                                </td>
                                <td className="px-3 py-1.5" style={{ color: "#64748B" }}>{r.unit || "—"}</td>
                                <td className="px-3 py-1.5">
                                  {r.status === "alto" && <span style={{ color: "#DC2626", fontWeight: "bold" }}>↑ Alto</span>}
                                  {r.status === "bajo" && <span style={{ color: "#D97706", fontWeight: "bold" }}>↓ Bajo</span>}
                                  {r.status === "normal" && <span style={{ color: "#16A34A" }}>✓</span>}
                                  {!r.status && "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="p-3 bg-amber-50/70 border-t border-amber-200/60 flex items-start gap-2 text-[11px] text-amber-900 leading-relaxed">
                          <ShieldAlert size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>Aviso Legal y Clínico:</strong> Este análisis es de carácter informativo y preventivo, basado en los rangos de referencia estándar. No constituye un diagnóstico médico definitivo ni una prescripción terapéutica. Le sugerimos consultar con su Médico General para una valoración clínica personalizada.
                          </span>
                        </div>
                      </div>
                    )}

                  </div>
                ))}
              </div>
            </div>
          )}

          {examsLoaded && exams.length === 0 && !examPreview && (
            <p className="text-xs text-center py-4" style={{ color: "#CBD5E1" }}>
              Aún no hay exámenes subidos
            </p>
          )}
        </Section>

        {/* ── 4. Mis datos ── */}
        <Section title="Mis datos y privacidad" icon={Download}>
          <p className="text-sm mb-4" style={{ color: "#475569" }}>
            Tus datos de salud son tuyos. Puedes exportarlos o eliminar tu cuenta en cualquier momento.
          </p>

          {/* Export — PDF (visual preview) */}
          <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: "#F1F5F9" }}>
            <div>
              <p className="text-sm font-medium" style={{ color: "#1E293B" }}>Reporte visual en PDF</p>
              <p className="text-xs" style={{ color: "#64748B" }}>
                Reporte ejecutivo imprimible con tus signos vitales, gráficos y recomendaciones
              </p>
            </div>
            <button
              onClick={() => navigate("/mi-cuenta/exportar")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: "linear-gradient(135deg, #00628F 0%, #883476 100%)",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
              data-testid="mi-cuenta-pdf-btn"
            >
              <Download size={14} /> Ver reporte PDF
            </button>
          </div>

          {/* Export — JSON (raw) */}
          <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: "#F1F5F9" }}>
            <div>
              <p className="text-sm font-medium" style={{ color: "#1E293B" }}>Exportar todos mis datos (JSON)</p>
              <p className="text-xs" style={{ color: "#64748B" }}>
                Perfil, formularios F1-F5, historial de mediciones — formato JSON crudo
              </p>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: exportDone ? "#DCFCE7" : "#EFF6FF",
                color: exportDone ? "#166534" : "#1D4ED8",
                border: `1px solid ${exportDone ? "#BBF7D0" : "#BFDBFE"}`,
                cursor: exporting ? "wait" : "pointer",
              }}
              data-testid="mi-cuenta-export-btn"
            >
              {exporting ? (
                <><Loader2 size={14} className="animate-spin" /> Exportando…</>
              ) : exportDone ? (
                <><CheckCircle size={14} /> Descargado</>
              ) : (
                <><Download size={14} /> Exportar</>
              )}
            </button>
          </div>

          {/* Delete */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium" style={{ color: "#DC2626" }}>Eliminar mi cuenta</p>
              <p className="text-xs" style={{ color: "#64748B" }}>
                Todos tus datos serán eliminados de forma permanente (30 días de gracia)
              </p>
            </div>
            <button
              onClick={() => setDeleteStep(1)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
              style={{ background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA", cursor: "pointer" }}
              data-testid="mi-cuenta-delete-btn"
            >
              <Trash2 size={14} /> Eliminar
            </button>
          </div>
        </Section>

        {/* ── 4. Próximamente (TODOs pendientes) ── */}
        <div
          className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4"
          data-testid="mi-cuenta-todos"
        >
          <button
            onClick={() => setShowTodos(!showTodos)}
            className="w-full flex items-center justify-between px-5 py-4"
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <div className="flex items-center gap-2">
              <Clock size={16} style={{ color: "#94A3B8" }} />
              <span className="font-semibold text-sm" style={{ color: "#64748B" }}>
                Funciones próximamente
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: "#F1F5F9", color: "#94A3B8" }}>
                4 pendientes
              </span>
            </div>
            {showTodos
              ? <ChevronUp size={16} style={{ color: "#94A3B8" }} />
              : <ChevronDown size={16} style={{ color: "#94A3B8" }} />}
          </button>

          {showTodos && (
            <div className="px-5 pb-4 space-y-2">
              {[
                {
                  title: "Verificación de email personal",
                  desc: "Tu email de respaldo se verificará enviándote un código al registrarlo.",
                  blocker: "Requiere servicio de email (P3)",
                  color: "#D97706",
                },
                {
                  title: "Verificación de cambio de email",
                  desc: "Al cambiar tu email de acceso, recibirás un enlace de confirmación.",
                  blocker: "Requiere servicio de email (P3)",
                  color: "#D97706",
                },
                {
                  title: "Sincronización de perfil con GCP",
                  desc: "Los cambios de perfil (nombre, datos biométricos) se sincronizarán automáticamente con Google Healthcare API.",
                  blocker: "Gateway en mantenimiento — endpoint PATCH /demographics",
                  color: "#7C3AED",
                },
                {
                  title: "Eliminación permanente y sincronización",
                  desc: "Al eliminar tu cuenta, la baja también se registrará en los sistemas de salud digitales.",
                  blocker: "Gateway en mantenimiento — endpoint DELETE /patients",
                  color: "#7C3AED",
                },
              ].map(({ title, desc, blocker, color }) => (
                <div key={title} className="flex items-start gap-3 px-3 py-2.5 rounded-xl" style={{ background: "#F8FAFC", border: "1px solid #F1F5F9" }}>
                  <Clock size={13} style={{ color, flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: "#475569" }}>{title}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>{desc}</p>
                    <p className="text-[10px] mt-1 font-medium" style={{ color }}>⏳ {blocker}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal eliminación — PASO 1 ── */}
      {deleteStep === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-testid="delete-modal-step1">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#FEE2E2" }}>
              <Trash2 size={22} style={{ color: "#DC2626" }} />
            </div>
            <h3 className="text-lg font-bold text-center mb-2" style={{ color: "#1E293B" }}>
              ¿Eliminar tu cuenta?
            </h3>
            <div className="text-sm space-y-2 mb-6" style={{ color: "#475569" }}>
              <p>Esto eliminará de forma <strong>permanente</strong>:</p>
              <ul className="list-disc pl-5 space-y-1 text-xs" style={{ color: "#64748B" }}>
                <li>Tu perfil y datos personales</li>
                <li>Todos los formularios F1–F5 completados</li>
                <li>Tu historial de mediciones de seguimiento</li>
                <li>Tu acceso a WE360</li>
              </ul>
              <p className="text-xs mt-2 p-2 rounded-lg" style={{ background: "#FFFBEB", color: "#92400E" }}>
                Tendrás <strong>30 días</strong> para contactar soporte y cancelar la eliminación.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteStep(0)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border"
                style={{ borderColor: "#E2E8F0", color: "#64748B" }}
                data-testid="delete-modal-cancel-1"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setDeleteStep(2); setDeleteInput(""); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: "#DC2626", color: "white", border: "none", cursor: "pointer" }}
                data-testid="delete-modal-continue"
              >
                Entiendo, continuar →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal eliminación — PASO 2 ── */}
      {deleteStep === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-testid="delete-modal-step2">
            <h3 className="text-lg font-bold mb-2" style={{ color: "#DC2626" }}>
              Confirmación final
            </h3>
            <p className="text-sm mb-4" style={{ color: "#475569" }}>
              Para confirmar, escribe{" "}
              <strong style={{ color: "#DC2626", fontFamily: "monospace" }}>ELIMINAR</strong>{" "}
              en el campo de abajo:
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="ELIMINAR"
              className="w-full px-4 py-3 rounded-xl border text-sm font-mono mb-4"
              style={{
                borderColor: deleteInput === "ELIMINAR" ? "#FCA5A5" : "#E2E8F0",
                color: "#DC2626",
                background: deleteInput === "ELIMINAR" ? "#FEF2F2" : "white",
              }}
              autoFocus
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteStep(0); setDeleteInput(""); }}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border"
                style={{ borderColor: "#E2E8F0", color: "#64748B" }}
                data-testid="delete-modal-cancel-2"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteInput !== "ELIMINAR" || deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                style={{
                  background: deleteInput === "ELIMINAR" ? "#DC2626" : "#F1F5F9",
                  color: deleteInput === "ELIMINAR" ? "white" : "#94A3B8",
                  border: "none",
                  cursor: deleteInput === "ELIMINAR" && !deleting ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                }}
                data-testid="delete-confirm-btn"
              >
                {deleting ? (
                  <><Loader2 size={14} className="animate-spin" /> Eliminando…</>
                ) : (
                  <><Trash2 size={14} /> Eliminar mi cuenta</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
